import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Package,
  Truck,
  AlertTriangle,
  Clock,
  XCircle,
  DollarSign,
  ArrowDownToLine,
  ArrowUpFromLine,
  Bell,
  TrendingUp,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total de Produtos",
      value: stats?.total_products || 0,
      icon: Package,
      color: "text-primary",
      bgColor: "bg-primary/10",
      link: "/products",
      description: "Ver todos os produtos",
    },
    {
      title: "Fornecedores",
      value: stats?.total_suppliers || 0,
      icon: Truck,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      link: "/suppliers",
      description: "Ver todos os fornecedores",
    },
    {
      title: "Estoque Baixo",
      value: stats?.low_stock_count || 0,
      icon: AlertTriangle,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      alert: stats?.low_stock_count > 0,
      link: "/products?status=low_stock",
      description: "Ver produtos com estoque baixo",
    },
    {
      title: "A Vencer (30 dias)",
      value: stats?.expiring_count || 0,
      icon: Clock,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      alert: stats?.expiring_count > 0,
      link: "/reports?tab=expiring",
      description: "Ver produtos a vencer",
    },
    {
      title: "Produtos Vencidos",
      value: stats?.expired_count || 0,
      icon: XCircle,
      color: "text-red-600",
      bgColor: "bg-red-50",
      alert: stats?.expired_count > 0,
      link: "/reports?tab=expired",
      description: "Ver produtos vencidos",
    },
    {
      title: "Valor do Estoque",
      value: formatCurrency(stats?.total_stock_value || 0),
      icon: DollarSign,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      link: "/reports?tab=stock-value",
      description: "Ver valor detalhado",
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
            Olá, {user?.name?.split(" ")[0]}!
          </h1>
          <p className="text-slate-500 mt-1">
            Aqui está o resumo do seu estoque hoje
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/stock-entry">
            <Button className="gap-2" data-testid="quick-entry-btn">
              <ArrowDownToLine className="h-4 w-4" />
              Nova Entrada
            </Button>
          </Link>
          <Link to="/stock-exit">
            <Button variant="outline" className="gap-2" data-testid="quick-exit-btn">
              <ArrowUpFromLine className="h-4 w-4" />
              Nova Saída
            </Button>
          </Link>
        </div>
      </div>

      {/* Notification Alert */}
      {stats?.unread_notifications > 0 && (
        <Link to="/notifications">
          <Card className="bg-amber-50 border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors">
            <CardContent className="flex items-center gap-4 py-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Bell className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-amber-900">
                  Você tem {stats.unread_notifications} notificação(ões) não lida(s)
                </p>
                <p className="text-sm text-amber-700">
                  Clique para ver os alertas de estoque
                </p>
              </div>
              <Badge variant="destructive">{stats.unread_notifications}</Badge>
            </CardContent>
          </Card>
        </Link>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <Card
            key={index}
            className={`card-dashboard cursor-pointer hover:shadow-lg hover:border-primary/30 transition-all duration-200 ${stat.alert ? "ring-2 ring-amber-300" : ""}`}
            data-testid={`stat-card-${index}`}
            onClick={() => navigate(stat.link)}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-2">
                    {stat.value}
                  </p>
                </div>
                <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
              {stat.alert && (
                <Badge className="mt-3 alert-badge-warning">
                  Atenção necessária
                </Badge>
              )}
              <p className="text-xs text-slate-400 mt-3 hover:text-primary">
                {stat.description} →
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Entries */}
        <Card className="card-dashboard">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg">
                  Últimas Entradas
                </CardTitle>
                <CardDescription>Movimentações recentes de entrada</CardDescription>
              </div>
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <ArrowDownToLine className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.recent_entries?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_entries.map((entry, index) => (
                  <div
                    key={entry.id || index}
                    className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Package className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {entry.product_name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {entry.supplier_name}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-emerald-600">
                        +{entry.quantity}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatDate(entry.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">
                Nenhuma entrada recente
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Exits */}
        <Card className="card-dashboard">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg">
                  Últimas Saídas
                </CardTitle>
                <CardDescription>Movimentações recentes de saída</CardDescription>
              </div>
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                <ArrowUpFromLine className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.recent_exits?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_exits.map((exit, index) => (
                  <div
                    key={exit.id || index}
                    className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Package className="h-5 w-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {exit.product_name}
                        </p>
                        <p className="text-sm text-slate-500">{exit.reason}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-red-600">
                        -{exit.quantity}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatDate(exit.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">
                Nenhuma saída recente
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Ações Rápidas</CardTitle>
          <CardDescription>Acesse as principais funcionalidades</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/products">
              <Button
                variant="outline"
                className="w-full h-24 flex flex-col gap-2"
                data-testid="quick-products-btn"
              >
                <Package className="h-6 w-6" />
                <span>Produtos</span>
              </Button>
            </Link>
            <Link to="/suppliers">
              <Button
                variant="outline"
                className="w-full h-24 flex flex-col gap-2"
                data-testid="quick-suppliers-btn"
              >
                <Truck className="h-6 w-6" />
                <span>Fornecedores</span>
              </Button>
            </Link>
            <Link to="/reports">
              <Button
                variant="outline"
                className="w-full h-24 flex flex-col gap-2"
                data-testid="quick-reports-btn"
              >
                <TrendingUp className="h-6 w-6" />
                <span>Relatórios</span>
              </Button>
            </Link>
            <Link to="/notifications">
              <Button
                variant="outline"
                className="w-full h-24 flex flex-col gap-2"
                data-testid="quick-notifications-btn"
              >
                <Bell className="h-6 w-6" />
                <span>Alertas</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
